'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { BUNDLED_FONTS } from './bundledFonts';

/**
 * Reading font, chosen by the reader.
 *
 * This app is a wall of text in a language the reader is still learning, often
 * for a long sitting. Which face that text is set in is not decoration — it is
 * the difference between comfortable and tiring, and the right answer differs
 * per person and per script. So it is a setting.
 *
 * The list comes from two places:
 *
 *  - **52 faces we ship** (`public/fonts`, declared in `app/fonts.css`). These
 *    work on every device, which is what stops the picker looking like a demo
 *    on a machine with a bare font set. They cost nothing until chosen —
 *    `unicode-range` means a file is fetched only when text needs a glyph from
 *    it, so a reader who never opens the dialog downloads one family.
 *  - **Everything already installed on the reader's machine.** A browser will
 *    not tell you what fonts exist, so they are *measured* — see `detect()`.
 *    On a typical desktop that adds another twenty to forty.
 *
 * Only system fonts that are actually present are offered. Listing Avenir to a
 * Windows user, or Calibri to a Mac user, produces a menu entry that silently
 * does nothing when clicked, which is worse than a shorter list.
 *
 * The applied family is stamped on <html> by the inline script below *before*
 * first paint, exactly like the theme. Doing it in an effect would repaint the
 * page in a different face a moment after load, which is far more jarring for a
 * font than for a colour.
 */

export const FONT_KEY = 'gt_font';
/**
 * The resolved stack, cached beside the id.
 *
 * The pre-paint script needs a font-family value before any bundle has loaded,
 * and the catalogue is ~90 entries — inlining all of them into a <script> in the
 * document head, on every page load, to look up one string is a bad trade.
 * Caching the answer keeps that script at a few lines. Both keys are written in
 * one place (`setFont`), so they cannot drift apart.
 */
export const FONT_STACK_KEY = 'gt_font_stack';
export const DEFAULT_FONT = 'inter';

export type FontCategory = 'sans' | 'serif' | 'mono' | 'display';

export interface FontOption {
  id: string;
  /** The family name, as the browser knows it. Also what is shown in the list. */
  label: string;
  category: FontCategory;
  /** Shipped by us, so guaranteed present — never filtered out by detection. */
  bundled?: boolean;
  /** Shown under the name. Only the bundled ones earn the space. */
  note?: string;
}

const FALLBACK: Record<FontCategory, string> = {
  sans: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "ui-monospace, 'SF Mono', Menlo, monospace",
  display: "system-ui, -apple-system, 'Segoe UI', sans-serif",
};

/**
 * The faces we ship — generated from scripts/fetch-fonts.py, so this list and
 * the @font-face rules in app/fonts.css can never disagree.
 *
 * 52 families, because a picker with six entries reads as a demo rather than a
 * setting. They cost nothing until chosen: `unicode-range` on every face means
 * the browser fetches a file only when text needs a glyph from it, so a reader
 * who never opens this dialog downloads exactly one family.
 */
const BUNDLED: FontOption[] = BUNDLED_FONTS.map((f) => ({ ...f, bundled: true }));

/**
 * Candidates to look for on the reader's machine.
 *
 * The Windows, macOS and common-Linux families people actually have — the ones
 * that ship with an OS or with Office, plus the handful that arrive with
 * browsers. Nothing here is downloaded; a name that is not installed simply
 * never appears in the list.
 */
const CANDIDATES: { label: string; category: FontCategory }[] = [
  // ── Sans ────────────────────────────────────────────────────────────────
  { label: 'Arial', category: 'sans' },
  { label: 'Arial Narrow', category: 'sans' },
  { label: 'Arial Black', category: 'sans' },
  { label: 'Helvetica', category: 'sans' },
  { label: 'Helvetica Neue', category: 'sans' },
  { label: 'Segoe UI', category: 'sans' },
  { label: 'Segoe UI Variable', category: 'sans' },
  { label: 'Calibri', category: 'sans' },
  { label: 'Candara', category: 'sans' },
  { label: 'Corbel', category: 'sans' },
  { label: 'Tahoma', category: 'sans' },
  { label: 'Verdana', category: 'sans' },
  { label: 'Trebuchet MS', category: 'sans' },
  { label: 'Franklin Gothic Medium', category: 'sans' },
  { label: 'Century Gothic', category: 'sans' },
  { label: 'Gill Sans', category: 'sans' },
  { label: 'Gill Sans MT', category: 'sans' },
  { label: 'Futura', category: 'sans' },
  { label: 'Avenir', category: 'sans' },
  { label: 'Avenir Next', category: 'sans' },
  { label: 'Optima', category: 'sans' },
  { label: 'Geneva', category: 'sans' },
  { label: 'Lucida Grande', category: 'sans' },
  { label: 'Lucida Sans Unicode', category: 'sans' },
  { label: 'Roboto', category: 'sans' },
  { label: 'Open Sans', category: 'sans' },
  { label: 'Lato', category: 'sans' },
  { label: 'Montserrat', category: 'sans' },
  { label: 'Source Sans Pro', category: 'sans' },
  { label: 'PT Sans', category: 'sans' },
  { label: 'Fira Sans', category: 'sans' },
  { label: 'Nunito', category: 'sans' },
  { label: 'Nunito Sans', category: 'sans' },
  { label: 'Ubuntu', category: 'sans' },
  { label: 'Cantarell', category: 'sans' },
  { label: 'DejaVu Sans', category: 'sans' },
  { label: 'Liberation Sans', category: 'sans' },
  { label: 'Noto Sans Display', category: 'sans' },
  { label: 'Aptos', category: 'sans' },
  { label: 'Bahnschrift', category: 'sans' },
  { label: 'MS Sans Serif', category: 'sans' },

  // ── Serif ───────────────────────────────────────────────────────────────
  { label: 'Times New Roman', category: 'serif' },
  { label: 'Times', category: 'serif' },
  { label: 'Georgia', category: 'serif' },
  { label: 'Garamond', category: 'serif' },
  { label: 'EB Garamond', category: 'serif' },
  { label: 'Palatino', category: 'serif' },
  { label: 'Palatino Linotype', category: 'serif' },
  { label: 'Book Antiqua', category: 'serif' },
  { label: 'Baskerville', category: 'serif' },
  { label: 'Baskerville Old Face', category: 'serif' },
  { label: 'Didot', category: 'serif' },
  { label: 'Cambria', category: 'serif' },
  { label: 'Constantia', category: 'serif' },
  { label: 'Bookman Old Style', category: 'serif' },
  { label: 'Century Schoolbook', category: 'serif' },
  { label: 'Rockwell', category: 'serif' },
  { label: 'Hoefler Text', category: 'serif' },
  { label: 'Charter', category: 'serif' },
  { label: 'Iowan Old Style', category: 'serif' },
  { label: 'American Typewriter', category: 'serif' },
  { label: 'Big Caslon', category: 'serif' },
  { label: 'PT Serif', category: 'serif' },
  { label: 'Merriweather', category: 'serif' },
  { label: 'Source Serif Pro', category: 'serif' },
  { label: 'DejaVu Serif', category: 'serif' },
  { label: 'Liberation Serif', category: 'serif' },
  { label: 'MS Serif', category: 'serif' },

  // ── Monospace ───────────────────────────────────────────────────────────
  { label: 'Courier New', category: 'mono' },
  { label: 'Courier', category: 'mono' },
  { label: 'Consolas', category: 'mono' },
  { label: 'Cascadia Code', category: 'mono' },
  { label: 'Cascadia Mono', category: 'mono' },
  { label: 'Monaco', category: 'mono' },
  { label: 'Menlo', category: 'mono' },
  { label: 'SF Mono', category: 'mono' },
  { label: 'Andale Mono', category: 'mono' },
  { label: 'Lucida Console', category: 'mono' },
  { label: 'Source Code Pro', category: 'mono' },
  { label: 'Fira Code', category: 'mono' },
  { label: 'IBM Plex Mono', category: 'mono' },
  { label: 'DejaVu Sans Mono', category: 'mono' },
  { label: 'Liberation Mono', category: 'mono' },
  { label: 'Ubuntu Mono', category: 'mono' },

  // ── Display & handwriting ───────────────────────────────────────────────
  { label: 'Impact', category: 'display' },
  { label: 'Comic Sans MS', category: 'display' },
  { label: 'Papyrus', category: 'display' },
  { label: 'Brush Script MT', category: 'display' },
  { label: 'Copperplate', category: 'display' },
  { label: 'Chalkboard SE', category: 'display' },
  { label: 'Marker Felt', category: 'display' },
  { label: 'Bradley Hand', category: 'display' },
  { label: 'Snell Roundhand', category: 'display' },
  { label: 'Segoe Print', category: 'display' },
  { label: 'Segoe Script', category: 'display' },
  { label: 'Ink Free', category: 'display' },
  { label: 'Luminari', category: 'display' },
  { label: 'Trattatello', category: 'display' },
];

export const CATEGORY_LABEL: Record<FontCategory, string> = {
  sans: 'Sans-serif',
  serif: 'Serif',
  mono: 'Monospace',
  display: 'Handwriting & display',
};

/** Stable id from a family name, so a stored choice survives list reordering. */
function slug(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

/**
 * Candidates minus anything already bundled.
 *
 * Several of these families — Roboto, Lato, Merriweather, Fira Code — are both
 * shipped by us AND commonly installed. Without this filter they would appear
 * twice in the picker, once guaranteed and once conditional, which reads as a
 * bug. The shipped copy wins because it renders the same for everyone.
 */
const BUNDLED_LABELS = new Set(BUNDLED.map((f) => f.label.toLowerCase()));

const SYSTEM: FontOption[] = CANDIDATES
  .filter((c) => !BUNDLED_LABELS.has(c.label.toLowerCase()))
  .map((c) => ({
    id: slug(c.label),
    label: c.label,
    category: c.category,
  }));

/**
 * Everything, bundled first. Detection decides which of the rest is shown.
 *
 * "System default" sits second rather than at the end of the shipped group: it
 * is the escape hatch from all of this, and someone looking for it is looking
 * near the top, not below fifty faces they have already decided against.
 */
export const ALL_FONTS: FontOption[] = [
  BUNDLED[0],
  {
    id: 'system',
    label: 'System default',
    category: 'sans',
    bundled: true,
    note: 'Whatever your device already uses.',
  },
  ...BUNDLED.slice(1),
  ...SYSTEM,
];

export function stackFor(f: FontOption): string {
  // 'system' is the absence of a choice, so it is the fallback chain alone.
  if (f.id === 'system') return FALLBACK.sans;
  return `'${f.label}', ${FALLBACK[f.category]}`;
}

export function fontById(id: string | null): FontOption {
  return ALL_FONTS.find((f) => f.id === id) ?? ALL_FONTS.find((f) => f.id === DEFAULT_FONT)!;
}

/**
 * Which of the candidate families this machine actually has.
 *
 * There is no API for it. The trick is that a font-family list falls through to
 * the next entry when a family is missing, so a string measured in
 * `"Candidate", monospace` comes out at *exactly* the monospace width when
 * Candidate is absent, and at some other width when it is present. Measured
 * against all three generics, because a candidate can coincidentally match one.
 *
 * Canvas rather than DOM nodes: one context, ~90 measurements, no layout.
 *
 * Privacy-hardened browsers (Firefox with resistFingerprinting, Safari's font
 * protections) deliberately defeat this and report everything as missing. That
 * is the right behaviour on their side, and the wrong list on ours — so if
 * almost nothing comes back, the check is treated as unavailable rather than as
 * an answer, and the full catalogue is offered instead. A dead entry beats an
 * empty menu.
 */
function detect(): Set<string> | null {
  const found = new Set<string>();
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return null;

    // Mixed widths and shapes: the more a string varies between faces, the less
    // likely a present font measures identically to the generic it falls back to.
    const SAMPLE = 'mmmmmmmmmmlliWWWW@#$%0Ogq';
    const SIZE = '72px';
    const GENERICS = ['monospace', 'serif', 'sans-serif'];

    const width = (family: string) => {
      ctx.font = `${SIZE} ${family}`;
      return ctx.measureText(SAMPLE).width;
    };

    const baseline = GENERICS.map(width);

    for (const f of SYSTEM) {
      const present = GENERICS.some(
        (g, i) => width(`'${f.label}', ${g}`) !== baseline[i],
      );
      if (present) found.add(f.id);
    }
  } catch {
    return null;
  }

  // Every desktop has Arial or Helvetica or Times. Two hits total means the
  // measurement was blocked, not that the machine is bare.
  return found.size >= 3 ? found : null;
}

/**
 * The pre-paint script.
 *
 * Reads the cached stack rather than resolving an id, so it stays this short —
 * see FONT_STACK_KEY.
 */
export const FONT_INIT_SCRIPT = `
(function(){
  try {
    var s = localStorage.getItem('${FONT_STACK_KEY}');
    if (s) {
      document.documentElement.style.setProperty('--font-sans', s);
      document.documentElement.setAttribute('data-font',
        localStorage.getItem('${FONT_KEY}') || '${DEFAULT_FONT}');
    }
  } catch (e) {}
})();
`;

/** Module-level, so the header button and the dialog agree without a provider. */
const listeners = new Set<() => void>();

export function useFont() {
  // Same dance as useTheme: render the default on the server and on the first
  // client pass so the markup matches, then read the real value in an effect.
  // The pre-paint script has already applied it, so nothing visibly changes.
  const [font, setFontState] = useState<string>(DEFAULT_FONT);
  const [installed, setInstalled] = useState<Set<string> | null>(null);
  const [detected, setDetected] = useState(false);

  useEffect(() => {
    const read = () => setFontState(fontById(localStorage.getItem(FONT_KEY)).id);
    read();
    listeners.add(read);
    return () => { listeners.delete(read); };
  }, []);

  useEffect(() => {
    // Deferred: ~90 canvas measurements are fast but they are not worth putting
    // in front of the first paint of a page nobody has opened the dialog on yet.
    const t = setTimeout(() => {
      setInstalled(detect());
      setDetected(true);
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const setFont = useCallback((id: string) => {
    const chosen = fontById(id);
    const stack = stackFor(chosen);
    localStorage.setItem(FONT_KEY, chosen.id);
    localStorage.setItem(FONT_STACK_KEY, stack);
    document.documentElement.style.setProperty('--font-sans', stack);
    document.documentElement.setAttribute('data-font', chosen.id);
    listeners.forEach((fn) => fn());
  }, []);

  /**
   * What the dialog lists: everything we ship, plus everything measured on this
   * machine. Before detection finishes — and on a browser that blocks it — the
   * full catalogue shows, so the list is never suspiciously short.
   */
  const fonts = useMemo(
    () => ALL_FONTS.filter((f) => f.bundled || !installed || installed.has(f.id)),
    [installed],
  );

  return { font, setFont, fonts, detected };
}
